/**
 * The wire format between phone and server, and every shared limit. The client
 * and server import the same file, so a protocol change is one edit.
 */

import type { Seat, SeatActionBody, GameEvent } from './engine.js';
import type { View } from './view.js';

/** Bump when a message shape changes. Mismatch → the client reloads once. */
export const PROTO = 1;

export const MAX_MESSAGE_BYTES = 8 * 1024;
export const CODE_LENGTH = 5;
/** No vowels (and no Y): a code can never spell a word by accident. */
export const CODE_ALPHABET = 'BCDFGHJKLMNPQRSTVWXZ';
export const MAX_NAME_CHARS = 20;

/** Per-connection token bucket. */
export const RATE_LIMIT_MESSAGES = 20;
export const RATE_LIMIT_WINDOW_MS = 10_000;

export type ClientMsg =
  | { t: 'hello'; proto: number; token?: string }
  | { t: 'create'; name?: string }
  | { t: 'join'; code: string; name?: string }
  | { t: 'act'; seq: number; action: SeatActionBody }
  | { t: 'rematch' }
  | { t: 'end' } // remaining player ends an abandoned game
  | { t: 'leave' };

export type ErrorCode =
  | 'PROTO_MISMATCH'
  | 'NO_ROOM'
  | 'ROOM_FULL'
  | 'STALE_SEQ'
  | 'ILLEGAL'
  | 'NOT_YOUR_TURN'
  | 'RATE_LIMITED'
  | 'BAD_INPUT'
  | 'TRY_LATER'
  | 'NOT_IN_ROOM';

export const ERROR_TEXT: Record<ErrorCode, string> = {
  PROTO_MISMATCH: 'This app is out of date. Reloading…',
  NO_ROOM: 'No table with that code. Check the letters and try again.',
  ROOM_FULL: 'No table with that code. Check the letters and try again.', // uniform with NO_ROOM, on purpose
  STALE_SEQ: 'That move arrived late. The table has moved on.',
  ILLEGAL: 'That play is not allowed.',
  NOT_YOUR_TURN: 'Not your turn.',
  RATE_LIMITED: 'Slow down a little.',
  BAD_INPUT: 'The server did not understand that.',
  TRY_LATER: 'Too many attempts. Try again in a few minutes.',
  NOT_IN_ROOM: 'You are not at a table.',
};

export type Presence = 'present' | 'away' | 'gone';

export interface RoomInfo {
  code: string;
  seat: Seat;
  names: [string, string];
  /** Opponent's connection state. */
  opp: Presence;
  /** When the opponent went away (ms since epoch), for the "end game" offer. */
  oppAwaySince: number | null;
  /** Whether the host still waits for a second player. */
  waiting: boolean;
  /** Rematch requests, by seat. */
  rematch: [boolean, boolean];
}

export type ServerMsg =
  | { t: 'welcome'; proto: number; token: string; room: RoomInfo; view: View | null }
  | { t: 'view'; view: View; events: GameEvent[]; room: RoomInfo }
  | { t: 'room'; room: RoomInfo }
  | { t: 'error'; code: ErrorCode; message: string; seq?: number }
  | { t: 'ended'; reason: 'abandoned' | 'left' | 'expired' };

export function normalizeCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z]/g, '').slice(0, CODE_LENGTH);
}

export function isValidCode(code: string): boolean {
  return code.length === CODE_LENGTH && [...code].every((ch) => CODE_ALPHABET.includes(ch));
}

export function cleanName(raw: unknown, fallback: string): string {
  if (typeof raw !== 'string') return fallback;
  // Letters, numbers, spaces and a few friendly marks. Nothing that renders as
  // markup, nothing invisible.
  const s = raw.replace(/[^\p{L}\p{N} '._-]/gu, '').replace(/\s+/g, ' ').trim().slice(0, MAX_NAME_CHARS);
  return s || fallback;
}

/** Strict shape check for inbound messages. Unknown fields are rejected. */
export function parseClientMsg(raw: unknown): ClientMsg | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  const keys = Object.keys(m);
  const only = (...allowed: string[]) => keys.every((k) => allowed.includes(k));
  switch (m.t) {
    case 'hello':
      if (!only('t', 'proto', 'token')) return null;
      if (typeof m.proto !== 'number') return null;
      if (m.token !== undefined && (typeof m.token !== 'string' || m.token.length > 64)) return null;
      return { t: 'hello', proto: m.proto, token: m.token as string | undefined };
    case 'create':
      if (!only('t', 'name')) return null;
      if (m.name !== undefined && (typeof m.name !== 'string' || m.name.length > 64)) return null;
      return { t: 'create', name: m.name as string | undefined };
    case 'join':
      if (!only('t', 'code', 'name')) return null;
      if (typeof m.code !== 'string' || m.code.length > 16) return null;
      if (m.name !== undefined && (typeof m.name !== 'string' || m.name.length > 64)) return null;
      return { t: 'join', code: m.code, name: m.name as string | undefined };
    case 'act': {
      if (!only('t', 'seq', 'action')) return null;
      if (typeof m.seq !== 'number' || !Number.isInteger(m.seq)) return null;
      const a = parseSeatAction(m.action);
      if (!a) return null;
      return { t: 'act', seq: m.seq, action: a };
    }
    case 'rematch':
    case 'end':
    case 'leave':
      if (!only('t')) return null;
      return { t: m.t };
    default:
      return null;
  }
}

function isCardNum(x: unknown): x is number {
  return typeof x === 'number' && Number.isInteger(x) && x >= 0 && x <= 51;
}

export function parseSeatAction(raw: unknown): SeatActionBody | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  const keys = Object.keys(a);
  switch (a.t) {
    case 'discard':
      if (keys.length !== 2 || !Array.isArray(a.cards) || a.cards.length !== 2) return null;
      if (!isCardNum(a.cards[0]) || !isCardNum(a.cards[1])) return null;
      return { t: 'discard', cards: [a.cards[0], a.cards[1]] };
    case 'play':
      if (keys.length !== 2 || !isCardNum(a.card)) return null;
      return { t: 'play', card: a.card };
    case 'cut':
    case 'go':
    case 'ack':
      if (keys.length !== 1) return null;
      return { t: a.t };
    default:
      return null;
  }
}
